// @ts-strict-ignore
import Upload, { UploadChangeParam } from 'antd/lib/upload';
import * as React from 'react';
import { IntegrationType, RunwayResponse } from '../types';
import { UploadFile } from 'antd/lib/upload/interface';
import { withAuthorizationHeaders } from '../api';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import { selectAuthCredentials } from '../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { uploadFileTypeForIntegrationType } from '../helpers';
import { AppComponentProps } from '../typescriptHelpers';
import { appEndpoint } from '../helpers/apiHelpers';
import { globalMessage } from '../utils/globalMessage';
import { antGrey } from './styles/globalStyles';
import { displayNameForIntegrationType } from '../helpers/integrationHelpers';

interface UploadFileModalComponentProps extends AppComponentProps {
    integrationId: IntegrationType;
    fileList: UploadFile<unknown>[];
    onUpdateFileList: (integrationId: IntegrationType, fileList: UploadFile<unknown>[]) => void;
}

const UploadFileComponent = (props: UploadFileModalComponentProps) => {
    const appUrl = appEndpoint(props.orgId, props.appId);
    const uploadUrl = `${window.location.origin}/${appUrl}/integration/file`;
    const acceptedFileTypes = uploadFileTypeForIntegrationType(props.integrationId);
    const extraData = {
        integrationId: props.integrationId,
    };

    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const onChange = (info: UploadChangeParam) => {
        const { file, fileList } = info;

        if (file.status === 'done') {
            const response = file.response as RunwayResponse;

            if (response.success) {
                globalMessage.success('Uploaded!');
            } else {
                const error = response.errorMessage;

                globalMessage.error(error);
            }
        } else if (file.status === 'error') {
            globalMessage.error('Error uploading file. Please try again');
        }

        // Only allow one file at a time, take the last file uploaded
        const limitedFileList = fileList.length > 0 ? [fileList.pop()] : [];

        props.onUpdateFileList(props.integrationId, limitedFileList);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {descriptionForIntegrationType(props.integrationId)}
            <div style={{ marginTop: 24, marginBottom: 24, alignSelf: 'stretch' }}>
                <Upload.Dragger
                    accept={acceptedFileTypes}
                    action={uploadUrl}
                    headers={withAuthorizationHeaders(authCredentials, {})}
                    data={extraData}
                    onChange={onChange}
                    fileList={props.fileList}
                    style={{
                        borderWidth: 4,
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            height: 120,
                            alignItems: 'center',
                        }}
                    >
                        <Text style={{ color: antGrey.grey7 }}>Click or drag to upload</Text>
                    </div>
                </Upload.Dragger>
            </div>
            <Text type="secondary" style={{ textAlign: 'center' }}>
                We take security seriously.{' '}
                <Link href="https://www.runway.team/security" target="_blank">
                    Read more
                </Link>{' '}
                about how we store sensitive information.
            </Text>
        </div>
    );
};

export default UploadFileComponent;

const descriptionForIntegrationType = (integrationType: IntegrationType) => {
    switch (integrationType) {
        case IntegrationType.TestFlight:
        case IntegrationType.Apple:
            return (
                <Text style={{ textAlign: 'center' }}>
                    Upload your private key (.p8) to complete your App Store Connect setup. You can generate this file
                    in your
                    <Link href="https://appstoreconnect.apple.com/access/integrations/api" target="_blank">
                        &nbsp;App Store Connect account
                    </Link>
                    .
                </Text>
            );
        case IntegrationType.FirebaseBeta:
        case IntegrationType.GoogleBeta:
        case IntegrationType.Google:
            return (
                <Text style={{ textAlign: 'center' }}>
                    Create a service account on the Google Play Developer Console to generate a key file (for more
                    details, visit our{' '}
                    <Link href="https://docs.runway.team/integrations/app-stores/google-play-console" target="_blank">
                        docs
                    </Link>
                    ). Upload your key file (.json) to complete your {displayNameForIntegrationType(integrationType)}{' '}
                    setup.
                </Text>
            );
        case IntegrationType.Firebase:
            return (
                <Text style={{ textAlign: 'center' }}>
                    Create a service account on the{' '}
                    <Link href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank">
                        Google Cloud Platform console{' '}
                    </Link>
                    to generate a key file. Upload your key file (.json) to complete your Firebase setup. For further
                    setup instructions, visit our{' '}
                    <Link href="https://docs.runway.team/integrations/stability-monitoring/firebase" target="_blank">
                        documentation
                    </Link>
                    .
                </Text>
            );
        case IntegrationType.AndroidVitals:
            return (
                <Text style={{ textAlign: 'center' }}>
                    Create a service account on the{' '}
                    <Link href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank">
                        Google Cloud Platform console{' '}
                    </Link>
                    to generate a key file. Upload your key file (.json) to complete your Android Vitals setup. For
                    further setup instructions, visit our{' '}
                    <Link
                        href="https://docs.runway.team/integrations/observability-and-analytics/android-vitals"
                        target="_blank"
                    >
                        documentation
                    </Link>
                    .
                </Text>
            );
        case IntegrationType.GoogleCalendar:
            return (
                <Text style={{ textAlign: 'center' }}>
                    Create a service account on the{' '}
                    <Link href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank">
                        Google Cloud Platform console{' '}
                    </Link>
                    to generate a key file. Upload your key file (.json) to complete your Google Calendar setup. For
                    further setup instructions, visit our{' '}
                    <Link href="https://docs.runway.team/integrations/calendar/google-calendar" target="_blank">
                        documentation
                    </Link>
                    .
                </Text>
            );
        case IntegrationType.Samsung:
            return (
                <Text style={{ textAlign: 'center' }}>
                    Create a service account on the{' '}
                    <Link href="https://seller.samsungapps.com/" target="_blank">
                        Samsung Galaxy Store Portal
                    </Link>{' '}
                    to generate a key file. Upload your key file (.txt) to complete your Samsung setup. For further
                    instructions, visit our{' '}
                    <Link href="https://docs.runway.team/integrations/app-stores/samsung-galaxy-store" target="_blank">
                        documentation
                    </Link>
                    .
                </Text>
            );
        default:
            return null;
    }
};
